/**
 * Tournament Sweepstakes Calculator
 *
 * Awards points to every fighter for their whole-TOURNAMENT performance (across every
 * discipline they compete in), then sums those points per school (fighter affiliation)
 * to rank schools for a sweepstakes award. This runs at the event level, not per-discipline
 * — a fighter who fences both Longsword and Rapier gets credit toward their school's total
 * from both.
 *
 * A fighter's total = sum, across every discipline they appear in, of pool points +
 * elimination bonus for that discipline:
 *
 * - Pool points: each fighter's normalized P/I (win=3, tie=1, loss=0) from
 *   calculateCombinedStandings — normalized so fighters in a smaller pool (fewer
 *   round-robin bouts) aren't penalized relative to fighters in a bigger pool, and
 *   summed across pool round 1 + round 2 when a discipline runs two pool rounds.
 * - Elimination bonus: a flat bonus based on how far the fighter got in that discipline's
 *   bracket, on top of their pool points (not instead of them) — a fighter who fought hard
 *   through pools still gets credit for that even if they lose their first bracket bout.
 */
import { calculateCombinedStandings } from "./standings";

// Indexed by "rounds short of winning the final": 0 = champion, 1 = runner-up
// (lost the final), 2 = lost the semis, 3 = lost the quarters. Anything earlier
// than that falls back to a flat participation bonus for having made the bracket.
export const ELIMINATION_TIER_POINTS = [15, 10, 6, 3];
export const ELIMINATION_TIER_FALLBACK = 1;
export const ELIMINATION_TIER_LABELS = ["Champion", "Runner-up", "Semi-finalist", "Quarter-finalist"];
export const ELIMINATION_TIER_FALLBACK_LABEL = "Earlier bracket round";

/**
 * @param {object[]} disciplines - event's disciplines, each { id, pools, elimination }
 * @param {object[]} bouts - all bouts for the event (every discipline, pool + elimination, any status)
 * @param {object} fighterAffiliations - event-level { [name]: school }, the single source of
 *   truth for fighter affiliation (event.fighterAffiliations) — no longer read per-discipline,
 *   since a fighter's school doesn't change based on which discipline they're competing in.
 * @returns {{
 *   schools: { school: string, rank: number, fighterCount: number, poolPoints: number, eliminationPoints: number, totalPoints: number, fighters: object[] }[],
 *   fighters: { name: string, school: string, poolPoints: number, eliminationPoints: number, totalPoints: number }[],
 * }}
 */
export function calculateSweepstakes(disciplines, bouts, fighterAffiliations = {}) {
  const fighterMap = {}; // name -> { name, school, poolPoints, eliminationPoints }

  (disciplines || []).forEach((disc) => {
    const pools = (disc.pools || []).filter((p) => (p.fighters || []).length > 0);
    if (!pools.length) return;
    const discBouts = (bouts || []).filter((b) => b.disciplineId === disc.id);

    // ─── pool points for this discipline ───
    const poolBouts = discBouts.filter((b) => b.phase === "pool" && b.status === "confirmed");
    const poolStandings = calculateCombinedStandings(pools, poolBouts);
    const poolPointsByFighter = {};
    poolStandings.forEach((s) => { poolPointsByFighter[s.name] = s.PI; });

    // ─── elimination bonus for this discipline: furthest round reached ───
    const elimBouts = discBouts.filter((b) => b.phase === "elimination" && b.status === "confirmed" && b.confirmedResult);
    const stageConfig = disc.elimination?.stageConfig || [];
    const totalRounds = stageConfig.length;
    const furthest = {}; // name -> { roundIndex, won }
    elimBouts.forEach((bout) => {
      const roundIndex = bout.eliminationRound ?? 0;
      [bout.fighterBlue, bout.fighterRed].forEach((name) => {
        if (!name || name === "BYE") return;
        const won = bout.confirmedResult.winner === name;
        const existing = furthest[name];
        if (!existing || roundIndex > existing.roundIndex) furthest[name] = { roundIndex, won };
        else if (roundIndex === existing.roundIndex) existing.won = existing.won || won;
      });
    });

    const eliminationPointsByFighter = {};
    if (totalRounds > 0) {
      const finalRoundIndex = totalRounds - 1;
      Object.entries(furthest).forEach(([name, info]) => {
        const tier = info.roundIndex === finalRoundIndex
          ? (info.won ? 0 : 1)
          : finalRoundIndex - info.roundIndex + 1;
        eliminationPointsByFighter[name] = ELIMINATION_TIER_POINTS[tier] ?? ELIMINATION_TIER_FALLBACK;
      });
    }

    // ─── fold this discipline's points into each fighter's tournament-wide total ───
    const namesInDiscipline = new Set([...Object.keys(poolPointsByFighter), ...Object.keys(eliminationPointsByFighter)]);
    namesInDiscipline.forEach((name) => {
      if (!fighterMap[name]) fighterMap[name] = { name, school: "Unaffiliated", poolPoints: 0, eliminationPoints: 0 };
      const entry = fighterMap[name];
      entry.poolPoints += poolPointsByFighter[name] ?? 0;
      entry.eliminationPoints += eliminationPointsByFighter[name] ?? 0;
    });
  });

  Object.values(fighterMap).forEach((entry) => {
    if (fighterAffiliations[entry.name]) entry.school = fighterAffiliations[entry.name];
  });

  const fighters = Object.values(fighterMap)
    .map((f) => ({ ...f, totalPoints: Math.round((f.poolPoints + f.eliminationPoints) * 100) / 100 }))
    .sort((a, b) => b.totalPoints - a.totalPoints);

  // ─── group per school ───
  const schoolMap = {};
  fighters.forEach((f) => {
    if (!schoolMap[f.school]) schoolMap[f.school] = { school: f.school, fighterCount: 0, poolPoints: 0, eliminationPoints: 0, totalPoints: 0, fighters: [] };
    const s = schoolMap[f.school];
    s.fighterCount += 1;
    s.poolPoints += f.poolPoints;
    s.eliminationPoints += f.eliminationPoints;
    s.totalPoints += f.totalPoints;
    s.fighters.push(f);
  });

  const schools = Object.values(schoolMap)
    .map((s) => ({
      ...s,
      poolPoints: Math.round(s.poolPoints * 100) / 100,
      totalPoints: Math.round(s.totalPoints * 100) / 100,
      fighters: s.fighters.sort((a, b) => b.totalPoints - a.totalPoints),
    }))
    .sort((a, b) => b.totalPoints - a.totalPoints);
  schools.forEach((s, i) => { s.rank = i + 1; });

  return { schools, fighters };
}
